import { format } from 'date-fns/format';
import { PATTERN } from '@/modules/common/constants';
import { webSDK } from '@/utils/webSdk';
import { Storage } from '@pw-tech/web-sdk';
import { resetFaroUser } from '@/lib/faro';

export const logout = () => {
  resetFaroUser();
  webSDK?.logout();
};

export const _generateUTMQuery = (): string => {
  let _finalUTMQuery = '';
  const utmData = JSON.parse(sessionStorage.getItem('query') || Storage.get('utmParams') || '{}');
  if (Object.keys(utmData).length > 0) {
    _finalUTMQuery = `?source=${utmData?.utm_source}&medium=${utmData?.utm_medium}&campaign=${utmData?.utm_campaign}`;
  }

  return _finalUTMQuery;
};

export const formatDateString = (dateString: any, formatString: string) => {
  if (!dateString) {
    return '';
  }
  const date = new Date(dateString);

  // Format the date with date-fns
  const formattedDate = format(date, formatString);

  return formattedDate;
};

export const formatExamDate = (dateString: string) => {
  const date = new Date(dateString);
  const dayNumber = date.getDate();

  // Get ordinal suffix
  const suffix =
    dayNumber > 3 && dayNumber < 21 ? 'th' : ['th', 'st', 'nd', 'rd'][dayNumber % 10] || 'th';

  return {
    day: date.toLocaleDateString('en-US', { weekday: 'short' }), // "Thu"
    date: dayNumber, // 24
    month: date.toLocaleDateString('en-US', { month: 'short' }), // "Jul"
    forSubmitBtnText: `${dayNumber}${suffix} ${date.toLocaleDateString('en-US', {
      month: 'short',
    })}`,
  };
};

export const getEmailFromCredential = (credential?: string): string => {
  if (!credential) return '';
  try {
    const payload = credential.split('.')[1];
    const normalized = payload
      .replace(new RegExp(PATTERN.base64UrlDash, 'g'), '+')
      .replace(new RegExp(PATTERN.base64UrlUnderscore, 'g'), '/');
    const decoded = decodeURIComponent(
      atob(normalized)
        .split('')
        .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    );
    return JSON.parse(decoded)?.email ?? '';
  } catch {
    return '';
  }
};

export const maskPhoneNumber = (phone?: string): string => {
  const value = (phone ?? '').trim();
  if (!value) return '';

  if (new RegExp(PATTERN.maskedPhone).test(value)) {
    return new RegExp(PATTERN.leadingPlus).test(value) ? value : `+91 ${value}`;
  }

  let digits = value.replace(new RegExp(PATTERN.nonDigit, 'g'), '');
  if (!digits) return '';

  if (digits.length > 10) {
    digits = digits.slice(-10);
  }

  const lastFour = digits.slice(-4);
  const masked = 'X'.repeat(Math.max(0, digits.length - 4)) + lastFour;
  return `+91 ${masked}`;
};

export const buildConflictMessage = (phoneNumber?: string, fallback?: string): string => {
  const maskedPhone = maskPhoneNumber(phoneNumber);
  if (maskedPhone) {
    return `This email already has an IOI account linked to phone ${maskedPhone}. Please sign in with a different Google account to continue this application.`;
  }
  return (
    fallback ||
    'This email is associated with another account. Please sign in with a different Google account to continue this application.'
  );
};

/**
 * Waits for Razorpay SDK to be loaded and available
 * @param timeout Maximum time to wait in milliseconds (default: 10000ms)
 * @returns Promise that resolves when Razorpay is available
 */
export const waitForRazorpay = (timeout = 10000): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('Razorpay SDK can only be loaded in the browser.'));
      return;
    }

    if (window.Razorpay) {
      resolve();
      return;
    }

    const startTime = Date.now();
    const checkInterval = setInterval(() => {
      if (window.Razorpay) {
        clearInterval(checkInterval);
        resolve();
      } else if (Date.now() - startTime > timeout) {
        clearInterval(checkInterval);
        reject(
          new Error(
            'Razorpay SDK failed to load. Please check your internet connection and try again.'
          )
        );
      }
    }, 100);
  });
};
